import { useState } from 'react'
import { publishUntestedPackage, withdrawUntestedPackage } from '../services/api'
import { errorText } from '../utils/errorText'
import type { PackagingJob, UninstallFailure } from '../types/job'

/**
 * What the failed uninstall test showed, as label and value rows: the uninstall command, its exit
 * code and what the code means, and what was still on the test machine afterwards.
 */
export function uninstallFacts(u: UninstallFailure | null | undefined): [string, string][] {
  if (!u) return []
  const rows: [string, string][] = []
  if (u.command) rows.push(['Uninstall command', u.command])
  if (u.exit_code !== null && u.exit_code !== undefined) {
    rows.push(['Exit code', u.exit_meaning ? `${u.exit_code}: ${u.exit_meaning}` : String(u.exit_code)])
  }
  if (u.still_detected) {
    rows.push(['Still detected', u.detection_match || 'The detection rule still found the app after the uninstall'])
  }
  if (u.remaining_entries?.length) rows.push(['Still in Programs and Features', u.remaining_entries.join('; ')])
  if (u.remaining_folders?.length) rows.push(['Files left in', u.remaining_folders.join('; ')])
  return rows
}

export const NO_UNINSTALL_COMMAND = 'No uninstall command was included in this package, so uninstalling it ' +
  'from Intune will not remove the app.'

function UninstallDetails({ uninstall, reason }: { uninstall: UninstallFailure | null | undefined; reason: string | null }) {
  const rows = uninstallFacts(uninstall)
  // The reason already carries the sentence when the backend wrote it; say it once.
  const noCommand = !!uninstall && !uninstall.command && !(reason || '').includes(NO_UNINSTALL_COMMAND)
  if (!rows.length && !noCommand) return null
  return (
    <>
      {rows.length > 0 && (
        <dl className="mt-2 grid grid-cols-1 gap-x-3 gap-y-1 text-sm sm:grid-cols-[max-content_1fr]">
          {rows.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="font-medium">{label}</dt>
              <dd className="break-all font-mono text-xs sm:text-sm">{value}</dd>
            </div>
          ))}
        </dl>
      )}
      {noCommand && <p className="mt-2 text-sm font-medium">{NO_UNINSTALL_COMMAND}</p>}
    </>
  )
}

/**
 * A package held for the user's decision: its test was blocked by the test environment or a
 * dependency, or the app installed and was detected but its uninstall was not verified. The user
 * may publish it anyway after confirming the acknowledgement, and withdraw it again while it is
 * not deployed.
 */
export default function DecisionPanel({ job, onDone }: { job: PackagingJob; onDone: () => void }) {
  const [confirmed, setConfirmed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const decision = job.decision
  if (!decision || !job.result_app_id) return null
  const appId = job.result_app_id
  const uninstallFailed = decision.blocked_by === 'uninstall_not_verified'

  const act = async (action: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await action()
      onDone()
    } catch (err: any) {
      setError(errorText(err, 'The request failed'))
    } finally {
      setBusy(false)
    }
  }

  if (decision.state === 'accepted') {
    return (
      <div className="mb-4 rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-amber-700 dark:text-amber-300">
        <h4 className="text-sm font-semibold">
          {uninstallFailed ? 'Published without a full test: uninstall not verified' : 'Published without a full test'}
        </h4>
        <p className="mt-1 text-sm">{decision.reason}</p>
        <UninstallDetails uninstall={decision.uninstall} reason={decision.reason} />
        {decision.accepted_by && (
          <p className="mt-1 text-xs">Published anyway by {decision.accepted_by}.</p>
        )}
        {error && <p className="mt-2 text-sm text-red-600 dark:text-red-300">{error}</p>}
        <button type="button" className="btn-outline btn-sm mt-2" disabled={busy}
          onClick={() => act(() => withdrawUntestedPackage(appId, job.job_id))}>
          {busy ? 'Withdrawing…' : 'Withdraw'}
        </button>
      </div>
    )
  }
  if (decision.state !== 'pending') return null
  return (
    <div className="mb-4 rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-amber-700 dark:text-amber-300">
      <h4 className="text-sm font-semibold">
        {uninstallFailed ? 'Needs your decision: uninstall not verified' : 'Needs your decision'}
      </h4>
      <p className="mt-1 text-sm">{decision.reason}</p>
      <UninstallDetails uninstall={decision.uninstall} reason={decision.reason} />
      <label className="mt-3 flex items-start gap-2 text-sm">
        <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
        <span>{decision.acknowledgement}</span>
      </label>
      {error && <p className="mt-2 text-sm text-red-600 dark:text-red-300">{error}</p>}
      <button type="button" className="btn-blue btn-sm mt-2" disabled={!confirmed || busy}
        onClick={() => act(() => publishUntestedPackage(appId, job.job_id))}>
        {busy ? 'Publishing…' : 'Publish anyway'}
      </button>
    </div>
  )
}
